import { DataType, QueryFilter, DataGridColumn } from "../queries/queries.models";
import { Many2ManyFilterItem } from "../../views/components/many-to-many-filter-selector/many-to-many-filter-selector";
import { BaseValidationResponse } from "../validations/validations.models";
import { ListDisplayHelper } from "../../helpers/list-display-helper";
import { StringUtilities } from "../../utilities/strings";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { DocHybridRule } from "../vessels/vessel.models";

export interface CompanyModel extends IEntityTypeIdentifier {
    companyId?: number;
    address?: string;
    companyRegistrationNumber?: string;
    controlCountryId?: number;
    countryId?: number
    description?: string;
    email?: string;
    facsimile?: string;
    foundedDate?: Date;
    fullName: string;
    owCode?: string;
    logoUrl?: string;
    parentCompanyId?: number;
    registrationCountryId?: number;
    residence?: string;
    shortCompanyName?: string;
    size?: number;
    structuredAddress?: string;
    telephone?: string;
    telex?: string;
    website?: string;
    hasS51?: boolean;
    hasS52?: boolean;
    activeVessels?: VesselRoleDetailModel[];
    pscWithDeficienciesPercentage?: number;
    pscWithDetentionsPercentage?: number;
    averageDeficiencies?: number;
    averageDeficienciesPerInspection?: number;
    averageDetentionPerShipInFleet?: number;
    affiliatedVesselCount?: number;
    registrationCountryName: string;
    controlCountryName: string;
    countryName: string;
    countryCode: string;
    documentsCount?: number;
    privateNotesCount?: number;
    hasCompanyAbandonmentofSeafarersOlderThanFiveYears?:boolean;
    hasDryBMSManagementAudit: boolean;
    hasTMSAManagementAudit: boolean;
    hasCrewWelfareManagementAudit: boolean;
    customerId?: number;
    docSubscore?: number;
    isUnknown?: boolean;
    numberOfVesselsHavingIncidentGracePeriod: number;
    docHybridRules: DocHybridRule[];
}

export interface GetCompanyListFlagsResponse {
    hasCompanyAbandonmentofSeafarersOlderThanFiveYears?:boolean;
    hasDryBMSManagementAudit: boolean;
    hasTMSAManagementAudit: boolean;
    hasCrewWelfareManagementAudit: boolean;
}

export interface CompanySubscoreDetails {
    companyId?: number;
    managedVesselCount?: number;
    managedSafetyRatingVesselCount?: number;
    scoringCategoryManagementCompany?: number;
    totalPsc?: number;
    totalIncidentsCatA?: number;
    totalIncidentsCatB?: number;
    totalIncidentsCatC?: number;
    totalTransparentIncidentsCatABC?: number;
    totalDeficiencies?: number;
    totalDetentions?: number;
    hadVesselsInLifetime?: boolean;
}

export interface CompanyCertificateModel {
    certificateUID: string;
    certificate: string;
    certificateMeta: CompanyCertificateMetaModel;
}

export interface CompanyCertificateMetaModel {
    logo: string;
    title: string;
    companyDescription: string;
}

export interface VesselRoleDetailModel {
    vesselRoleId: number;
    roleName: string;
    roleCode: string;
    roleValue: RoleValueModel;
}

export interface RoleValueModel {
    id?: number;
    value: string;
}

export interface GetCompanyResponseModel {
    company: CompanyModel;
    certificates: CompanyCertificateModel[];
    crewWelfareValidUntil: string;
    crewWelfareStatus: string;
    isLegacyCrewWelfare: boolean;
    crewWelfareGuid: string;
}

export enum CompanyStepEnum {
    CompanyDetails = 1,
    AffiliatedVessels = 2,
    DocActivities = 3,
    PscPerf = 4,
    VettingContacts = 5,
    CompanyLists = 6,
    DocPerformance = 7,
    GhgPerformance = 8,
    FbrPerformance = 9,
    Documents = 10,
    CompanyComments = 11,
    CrewWelfare = 12,
    Insights = 13
}

export interface CompanySearchODataModel {
    docCount: number;
}

export interface CompanyODataModel {
    companyId: number;
    fullName: string;
    controlCountryId: number;
    controlCountry: string;
    countryId: number;
    country: string;
    email: string;
    owCode: string;
    registrationCountryId: number;
    registrationCountry: string;
    docCount: number;
    hasDoc: boolean;
    hasBo: boolean;
    hasRo: boolean;
    hasCo: boolean;
    hasCm: boolean;
    hasTm: boolean;
}

export interface CompanyVettingContactModel {
    companyVettingContactId?: number;
    companyId: number;
    name: string;
    email?: string;
    phoneNumberCodeId?: number;
    phoneNumber?: string;
    phoneAreaCode?: string;
    isPrimaryContact: boolean;
    mobileNumberCodeId?: number;
    mobileAreaCode?: string;
    mobileNumber?: string;
    comment?: string;
    role?: string;
}

export interface SubmitManagementAuditRequest {
    auditType: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    companyName: string;
    companyType: string;
    country: string;
    companyAddress: string;
    contactNumber: string;
    contactEmail: string;
    communicationsConsent: boolean;
    agreeToTermsAndConditions: boolean;
}

export interface SaveCompanyVettingContactResponse extends BaseValidationResponse {
    generatedId?: number;
}

export enum VesselCompanyAssociationTypes {
    BO = 1,
    CM = 2,
    TM = 3,
    RO = 4,
    CO = 5,
    DOC = 6
}

export class CompaniesHelper {
    static getCompanyRoleFilter(setDefault: boolean = false) {
        const filterColumn: DataGridColumn =
        {
            dataType: DataType.Many2Many,
            value: null,
            name: "",
            title: "Roles",
            instancePerRow: true,
            sortOrder: 1,
            columnOrder: -1,
            isHidden: false,
            isSortable: false,
            parameter: () => {
                return Object.keys(VesselCompanyAssociationTypes).filter(x => isNaN(Number(x))).map(x => { return new Many2ManyFilterItem(VesselCompanyAssociationTypes[x], x, StringUtilities.camelize('has ' + x.toLowerCase().trim()), DataType.Boolean); })
            }
        };

        if (setDefault == true) {
            filterColumn.filters =
                [
                    QueryFilter.getQuery('hasDoc', true, 'eq', DataType.Boolean, true, false, 'company_role_filter'),
                    QueryFilter.getQuery('hasTm', true, 'eq', DataType.Boolean, false, false, 'company_role_filter')
                ];

            filterColumn.value = [VesselCompanyAssociationTypes.DOC, VesselCompanyAssociationTypes.TM];
            filterColumn["isAppliedMobile"] = true;
            filterColumn["mobileDescription"] = ListDisplayHelper.getListToStringFromIds(filterColumn.parameter(), filterColumn.value);
        }

        return [
            filterColumn
        ];
    }
}

export interface GetGhgPerformanceRequest {
    companyId: number;
    vesselIds: number[];
}